import { useEffect, useRef, useState } from "react";
import { Zap, Snowflake } from "lucide-react";
import { sfx } from "../../lib/sound";

// "Marble Dream"-stijl: dromerige pastel afdaling die de speler zelf speelt met
// power-ups (Boost ⚡ voor je eigen knikker, Vertraag ❄️ voor de rest).
// props: marbles [{code,name,color}], pickIndex, onDone(winnerIndex)

const TRACK_LEN = 100;          // abstracte lengte (progress 0..1 * TRACK_LEN)
const WORLD_LEN = 7600;         // wereld-pixels voor de baan
const N_LEGS = 5;
const BASE_SECONDS = 22;        // basis-finishtijd zonder boosts
const R = 17;

function terrainY(x) {
  return x * 0.28 + 72 * Math.sin(x * 0.0044) + 40 * Math.sin(x * 0.0122 + 1.1) + 18 * Math.sin(x * 0.025 + 2.3);
}

export function MarbleRace({ marbles, pickIndex, onDone }) {
  const canvasRef = useRef(null);
  const apiRef = useRef({});
  const [boosts, setBoosts] = useState(4);
  const [slows, setSlows] = useState(2);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let cw = 0, ch = 0;
    const resize = () => {
      cw = canvas.clientWidth; ch = canvas.clientHeight;
      canvas.width = cw * dpr; canvas.height = ch * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const n = marbles.length;
    const progs = new Array(n).fill(0);
    const mult = marbles.map((_, i) => i === pickIndex ? 0.95 : 0.94 + Math.random() * 0.14);
    const phaseOff = marbles.map(() => Math.random() * Math.PI * 2);
    const offX = marbles.map((_, i) => (i - (n - 1) / 2) * 26);
    const baseV = 1 / BASE_SECONDS;

    const imgs = marbles.map((m) => {
      const im = new Image(); im.crossOrigin = "anonymous";
      im.src = `https://flagcdn.com/w160/${m.code}.png`; return im;
    });

    // drijvende dromerige vormen
    const blobs = Array.from({ length: 14 }, () => ({
      x: Math.random() * 4000, y: Math.random() * 600 - 100, r: 30 + Math.random() * 70,
      c: ["#fbcfe8", "#c7d2fe", "#bae6fd", "#ddd6fe", "#fde68a"][Math.floor(Math.random() * 5)],
      pf: 0.2 + Math.random() * 0.3,
    }));

    let boostUntil = 0, slowUntil = 0;
    apiRef.current.boost = () => {
      if (!startedRef.current || boostsRef.current <= 0) return;
      boostsRef.current -= 1; setBoosts(boostsRef.current);
      boostUntil = performance.now() + 1300; sfx.go();
    };
    apiRef.current.slow = () => {
      if (!startedRef.current || slowsRef.current <= 0) return;
      slowsRef.current -= 1; setSlows(slowsRef.current);
      slowUntil = performance.now() + 1600; sfx.whoosh();
    };

    let phase = "count", countStart = performance.now(), last = performance.now();
    let finishAt = 0, winner = -1, curLeg = 1, particles = [], done = false;

    sfx.beep();
    let cv = 3;
    const ct = setInterval(() => { cv -= 1; if (cv > 0) sfx.beep(); else if (cv === 0) { sfx.go(); startedRef.current = true; setRunning(true); } else clearInterval(ct); }, 1000);

    const zoom = 1.0;
    const toScreen = (wx, wy, camX, camY) => ({ x: (wx - camX) * zoom + cw * 0.5, y: (wy - camY) * zoom + ch * 0.52 });

    let raf;
    const draw = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000); last = now;
      if (phase === "count" && (now - countStart) / 1000 >= 3) phase = "run";

      if (phase === "run") {
        const boosting = now < boostUntil, slowing = now < slowUntil;
        for (let i = 0; i < n; i++) {
          if (progs[i] >= 1) continue;
          let v = baseV * mult[i] * (1 + 0.12 * Math.sin(now / 1000 * 1.6 + phaseOff[i]));
          if (i === pickIndex && boosting) v *= 2.05;
          if (i !== pickIndex && slowing) v *= 0.4;
          progs[i] = Math.min(1, progs[i] + v * dt);
          if (progs[i] >= 1 && winner < 0) { winner = i; phase = "finish"; finishAt = now; sfx.win(); }
        }
      }
      if (phase === "finish" && now - finishAt > 1900 && !done) { done = true; cancelAnimationFrame(raf); onDone && onDone(winner); return; }

      let leader = 0; for (let i = 1; i < n; i++) if (progs[i] > progs[leader]) leader = i;
      const meanP = progs.reduce((a, b) => a + b, 0) / n;
      const camX = meanP * WORLD_LEN;
      const camY = terrainY(camX);
      const legNow = Math.min(N_LEGS, Math.floor(progs[leader] * N_LEGS) + 1);
      if (phase === "run" && legNow > curLeg) { curLeg = legNow; sfx.whoosh(); }

      // ---- dromerige lucht ----
      const sky = ctx.createLinearGradient(0, 0, 0, ch);
      sky.addColorStop(0, "#b9c7ff"); sky.addColorStop(0.5, "#d9c7ff"); sky.addColorStop(1, "#ffd3ec");
      ctx.fillStyle = sky; ctx.fillRect(0, 0, cw, ch);
      blobs.forEach((b) => {
        const sx = ((b.x - camX * b.pf) % (cw + 400) + cw + 400) % (cw + 400) - 200;
        ctx.globalAlpha = 0.5; ctx.fillStyle = b.c;
        ctx.beginPath(); ctx.arc(sx, b.y + ch * 0.2, b.r, 0, Math.PI * 2); ctx.fill();
      });
      ctx.globalAlpha = 1;

      // ---- glanzende baan ----
      const xStart = camX - 1200, xEnd = camX + 1200;
      const surf = [];
      for (let wx = xStart; wx <= xEnd; wx += 20) surf.push(toScreen(wx, terrainY(wx), camX, camY));
      const linePath = () => { ctx.beginPath(); ctx.moveTo(surf[0].x, surf[0].y); surf.forEach((p) => ctx.lineTo(p.x, p.y)); };
      // grond onder de baan
      linePath(); ctx.lineTo(surf[surf.length - 1].x, ch + 60); ctx.lineTo(surf[0].x, ch + 60); ctx.closePath();
      const grnd = ctx.createLinearGradient(0, ch * 0.4, 0, ch);
      grnd.addColorStop(0, "rgba(167,139,250,0.25)"); grnd.addColorStop(1, "rgba(99,102,241,0.15)");
      ctx.fillStyle = grnd; ctx.fill();
      ctx.lineCap = "round"; ctx.lineJoin = "round";
      linePath(); ctx.strokeStyle = "#6d5fe0"; ctx.lineWidth = 42; ctx.stroke();      // schaduw
      linePath(); ctx.strokeStyle = "#8b7df5"; ctx.lineWidth = 32; ctx.stroke();      // body
      linePath(); ctx.strokeStyle = "#b9aaff"; ctx.lineWidth = 16; ctx.stroke();      // highlight
      linePath(); ctx.strokeStyle = "rgba(255,255,255,0.6)"; ctx.lineWidth = 4; ctx.stroke(); // glans

      // leg-poorten
      for (let lg = 1; lg < N_LEGS; lg++) {
        const gx = (lg / N_LEGS) * WORLD_LEN;
        if (gx < xStart || gx > xEnd) continue;
        const gp = toScreen(gx, terrainY(gx), camX, camY);
        ctx.fillStyle = "#fb7185"; ctx.fillRect(gp.x - 3, gp.y - 60, 6, 60);
        ctx.fillStyle = "#fda4af"; ctx.fillRect(gp.x - 3, gp.y - 60, 30, 16);
      }
      // finishpoort
      if (camX > WORLD_LEN - 1500) {
        const fp = toScreen(WORLD_LEN, terrainY(WORLD_LEN), camX, camY);
        for (let r2 = 0; r2 < 6; r2++) for (let c2 = 0; c2 < 2; c2++) { ctx.fillStyle = (r2 + c2) % 2 ? "#fff" : "#334155"; ctx.fillRect(fp.x - 12 + c2 * 12, fp.y - 84 + r2 * 14, 12, 14); }
      }

      // ---- knikkers ----
      const sorted = marbles.map((_, i) => i).sort((a, b) => progs[a] - progs[b]);
      sorted.forEach((i) => {
        const wx = progs[i] * WORLD_LEN + offX[i] * (1 - progs[i]);
        const sp = toScreen(wx, terrainY(wx), camX, camY);
        const p = toScreen(wx, terrainY(wx) - R, camX, camY);
        const r = R;
        ctx.fillStyle = "rgba(60,40,90,0.25)"; ctx.beginPath(); ctx.ellipse(sp.x, sp.y + 2, r, r * 0.35, 0, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 0.2; ctx.fillStyle = marbles[i].color; ctx.beginPath(); ctx.ellipse(p.x - r * 0.7, p.y, r * 1.3, r * 0.7, 0, 0, Math.PI * 2); ctx.fill(); ctx.globalAlpha = 1;
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate((progs[i] * WORLD_LEN) / R);
        ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.closePath(); ctx.clip();
        const im = imgs[i];
        if (im && im.complete && im.naturalWidth) { const s = Math.max((2 * r) / im.naturalWidth, (2 * r) / im.naturalHeight); ctx.drawImage(im, -im.naturalWidth * s / 2, -im.naturalHeight * s / 2, im.naturalWidth * s, im.naturalHeight * s); }
        else { ctx.fillStyle = marbles[i].color; ctx.fillRect(-r, -r, 2 * r, 2 * r); }
        ctx.restore();
        // glans-bol
        const gl = ctx.createRadialGradient(p.x - r * 0.4, p.y - r * 0.4, 1, p.x, p.y, r);
        gl.addColorStop(0, "rgba(255,255,255,0.55)"); gl.addColorStop(0.4, "rgba(255,255,255,0)");
        ctx.fillStyle = gl; ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, Math.PI * 2); ctx.fill();
        ctx.lineWidth = i === pickIndex ? 5 : 3; ctx.strokeStyle = i === pickIndex ? "#fbbf24" : marbles[i].color;
        ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, Math.PI * 2); ctx.stroke();
        if (i === pickIndex) { ctx.fillStyle = "#fbbf24"; ctx.font = "bold 15px sans-serif"; ctx.textAlign = "center"; ctx.fillText("★", p.x, p.y - r - 6); }
        if (phase === "run" && (i === pickIndex && now < boostUntil) && Math.random() < 0.6) particles.push({ x: p.x, y: p.y, vx: -3 - Math.random() * 3, vy: (Math.random() - 0.5) * 3, life: 1, c: "#fde68a" });
      });
      particles = particles.filter((q) => q.life > 0);
      particles.forEach((q) => { q.x += q.vx; q.y += q.vy; q.life -= 0.04; ctx.globalAlpha = q.life; ctx.fillStyle = q.c; ctx.beginPath(); ctx.arc(q.x, q.y, 3.5, 0, Math.PI * 2); ctx.fill(); });
      ctx.globalAlpha = 1;

      // ---- HUD ----
      ctx.fillStyle = "#fff"; ctx.strokeStyle = "rgba(0,0,0,0.25)"; ctx.lineWidth = 3;
      ctx.font = "bold 16px sans-serif"; ctx.textAlign = "left";
      ctx.strokeText(`RACE ${curLeg} / ${N_LEGS}`, 16, 28); ctx.fillText(`RACE ${curLeg} / ${N_LEGS}`, 16, 28);
      ctx.strokeText(`${Math.round(progs[leader] * 100)}%`, 16, 50); ctx.fillText(`${Math.round(progs[leader] * 100)}%`, 16, 50);
      ctx.textAlign = "right";
      ctx.strokeText(`Koploper: ${marbles[leader].name}`, cw - 16, 28); ctx.fillText(`Koploper: ${marbles[leader].name}`, cw - 16, 28);

      if (phase === "count") {
        ctx.fillStyle = "rgba(40,30,70,0.4)"; ctx.fillRect(0, 0, cw, ch);
        ctx.fillStyle = "#fff"; ctx.textAlign = "center"; ctx.font = "bold 90px sans-serif";
        const left = 3 - Math.floor((now - countStart) / 1000);
        ctx.fillText(left > 0 ? String(left) : "GO!", cw / 2, ch / 2 + 30);
      }
      if (phase === "finish") {
        ctx.fillStyle = "rgba(40,30,70,0.4)"; ctx.fillRect(0, 0, cw, ch);
        ctx.fillStyle = "#fbbf24"; ctx.textAlign = "center"; ctx.font = "bold 52px sans-serif"; ctx.fillText("FINISH! 🏆", cw / 2, ch / 2);
        ctx.fillStyle = "#fff"; ctx.font = "bold 26px sans-serif"; ctx.fillText(marbles[winner].name + " wint!", cw / 2, ch / 2 + 44);
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); clearInterval(ct); window.removeEventListener("resize", resize); };
    // eslint-disable-next-line
  }, []);

  // refs voor charges/started binnen de RAF
  const boostsRef = useRef(4), slowsRef = useRef(2), startedRef = useRef(false);
  useEffect(() => { boostsRef.current = boosts; }, [boosts]);
  useEffect(() => { slowsRef.current = slows; }, [slows]);

  return (
    <div className="relative">
      <canvas ref={canvasRef} data-testid="race-canvas" className="w-full rounded-3xl" style={{ height: "62vh", maxHeight: 560, display: "block", background: "#b9c7ff" }} />
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-4">
        <button onClick={() => apiRef.current.boost && apiRef.current.boost()} disabled={!running || boosts <= 0} data-testid="powerup-boost"
          className="relative flex flex-col items-center justify-center w-20 h-20 rounded-2xl border-4 border-white/70 shadow-xl active:scale-90 transition disabled:opacity-40"
          style={{ background: "linear-gradient(160deg,#fbbf24,#f97316)" }}>
          <Zap className="w-8 h-8 text-white" strokeWidth={2.5} fill="white" />
          <span className="text-[11px] font-black text-white">BOOST</span>
          <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white text-orange-600 text-sm font-black flex items-center justify-center shadow">{boosts}</span>
        </button>
        <button onClick={() => apiRef.current.slow && apiRef.current.slow()} disabled={!running || slows <= 0} data-testid="powerup-slow"
          className="relative flex flex-col items-center justify-center w-20 h-20 rounded-2xl border-4 border-white/70 shadow-xl active:scale-90 transition disabled:opacity-40"
          style={{ background: "linear-gradient(160deg,#38bdf8,#3b82f6)" }}>
          <Snowflake className="w-8 h-8 text-white" strokeWidth={2.5} />
          <span className="text-[11px] font-black text-white">VERTRAAG</span>
          <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white text-blue-600 text-sm font-black flex items-center justify-center shadow">{slows}</span>
        </button>
      </div>
    </div>
  );
}
